import React, { useState, useEffect } from "react"
import withStyles from "react-jss"
import colors from "../data/colors"
import { results } from "../data/results"
import {
  RESULTS_FADEIN,
  RESULTS_FADEOUT,
  SECTION_DURATION,
} from "../data/globalStyles"
import { RESULTS_RENDER_DATA, RESULT_IDS } from "../data/uniqueValues"
import { AnswerButton, CopyToClipboard } from "../components"

const styles = {
  container: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
    marginTop: "2rem",
    padding: "3rem",
    boxSizing: "border-box",
    "@media only screen and (max-width: 480px)": {
      marginTop: "1rem",
    },
    animation: ({ show }) =>
      `${
        show ? `$${RESULTS_FADEIN}` : `$${RESULTS_FADEOUT}`
      } ${SECTION_DURATION} ease-in-out`,
  },
  [`@keyframes ${RESULTS_FADEIN}`]: {
    from: {
      opacity: 0,
    },
    to: {
      opacity: 1,
    },
  },
  [`@keyframes ${RESULTS_FADEOUT}`]: {
    from: {
      opacity: 1,
    },
    to: {
      opacity: 0,
    },
  },
  about: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
    flex: 1,
    width: "100%",
    maxWidth: "1000px",
    "@media only screen and (max-width: 945px)": {
      maxWidth: "100%",
    },
  },
  heading: {
    color: colors.black,
    padding: "0 1rem",
    textAlign: "center",
    fontSize: "calc(22px + (26 - 16) * ((100vw - 300px) / (1600 - 300)))",
    marginBottom: "0.6em",
  },
  underline: {
    textDecoration: "underline",
    textDecorationColor: colors.orange,
  },
  description: {
    margin: 0,
    fontSize: "calc(16px + (18 - 12) * ((100vw - 300px) / (1600 - 300)))",
    textAlign: "center",
  },
  menu: {
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    flexDirection: "column",
    width: "100%",
    maxWidth: "1000px",
    "@media only screen and (max-width: 945px)": {
      maxWidth: "100%",
    },
    paddingLeft: "2em",
    "@media only screen and (max-width: 480px)": {
      paddingLeft: 0,
    },
  },
  subMenu: {
    margin: "1rem 0",
    width: "100%",
  },
  subMenuHeading: {
    fontSize: "calc(18px + (22 - 18) * ((100vw - 300px) / (1600 - 300)))",
  },
  dishesContainer: {
    display: "flex",
    flexFlow: "row wrap",
    boxSizing: "border-box",
    width: "100%",
  },
  dish: {
    position: "relative",
    width: "33%",
    textDecoration: "none",
    color: "black",
    boxSizing: "border-box",
    padding: "0 2em 3em 0 ",
    "&:hover $dishTitle": {
      textDecoration: "underline",
    },
    "@media only screen and (max-width: 860px)": {
      width: "47%",
    },
    "@media only screen and (max-width: 800px)": {
      padding: "0 1em 2em 0",
    },
    "@media only screen and (max-width: 480px)": {
      width: "100%",
      padding: "0 0 2em 0",
    },
  },
  imageContainer: {
    width: "100%",
  },
  image: {
    width: "100%",
    height: "100%",
    opacity: 1,
    objectFit: "cover",
    "&:hover": {
      opacity: 0.4,
    },
    transition: "opacity 0.5s",
  },
  dishTitle: {
    margin: "0.2rem 0",
    fontFamily: "SourceSerifPro",
    fontWeight: 700,
    fontSize: "calc(14px + (18 - 14) * ((100vw - 300px) / (1600 - 300)))",
  },
  credits: {
    display: "flex",
    flexDirection: "column",
    fontSize: "calc(12px + (14 - 12) * ((100vw - 300px) / (1600 - 300)))",
    marginTop: "50px",
    paddingTop: "50px",
    borderTop: "1px solid black",
    boxSizing: "border-box",
  },
}

const Results = ({
  classes,
  setShow,
  section,
  ids,
  onAnimationEnd,
  setResetQuiz,
  startWithResults,
}) => {
  const [renderData, setRenderData] = useState(RESULTS_RENDER_DATA)
  const [url, setUrl] = useState("https://")

  useEffect(() => {
    if (ids.length) {
      // query params having ids that don't belong in results break the app
      // filtering out stray ids
      const goodIds = ids.filter((d) => d !== 0 && RESULT_IDS.includes(d))

      const allResults = goodIds.map((id) =>
        results.find((d) => d.resultId === id)
      )

      let categoryWiseDishes = renderData.map((category) => ({
        ...category,
        dishes: allResults.filter((dish) =>
          category.shouldInclude.includes(dish.category)
        ),
      }))
      // `mains` dishes should come up first
      categoryWiseDishes = categoryWiseDishes.map((category) => {
        if (category.id === 2) {
          return {
            ...category,
            dishes: category.dishes.sort((a, _) =>
              a.category === "mains" ? -1 : 1
            ),
          }
        } else return category
      })
      setRenderData(categoryWiseDishes)

      const baseUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}`
      const query = goodIds.join(",")
      const copyUrl = `${baseUrl}?ids=${query}`
      setUrl(copyUrl)
      window.history.pushState("results url", "", copyUrl)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids])

  const restartQuiz = () => {
    // If URL contains query params for the results,
    // load the base URL without query params to start
    // the quiz from scratch
    if (startWithResults) {
      const url = `${window.location.protocol}//${window.location.host}${window.location.pathname}`
      window.location.href = url
      return
    }
    // reset all previous calculations
    setShow((d) => ({
      ...d,
      intro: true,
      results: false,
    }))
    setTimeout(() => {
      setRenderData(RESULTS_RENDER_DATA)
      setResetQuiz(true)
    }, 1000)

    if (window.parent && window.parent.dataLayer) {
      window.parent.dataLayer.push({
        event: "datavisualization-tgivingMenu-restart",
      })
    }
  }

  const openLink = (item) => {
    window.open(item, "_blank")
    if (window.parent && window.parent.dataLayer) {
      window.parent.dataLayer.push({
        event: "datavisualization-tgivingMenu-recipeClick",
        "gtm.elementUrl": item,
      })
    }
  }

  const renderDish = (key, dish) => {
    if (dish.noOfDishes === 3) {
      return (
        <>
          <a
            className={classes.dish}
            key={key}
            href={dish.url}
            onClick={() => openLink(dish.url)}
            target="_blank"
            rel="noreferrer">
            <div className={classes.imageContainer}>
              <img
                className={classes.image}
                src={dish.imgURL}
                alt={dish.result1}
              />
            </div>
            <p className={classes.dishTitle}>{dish.result1}</p>
          </a>
          <a
            className={classes.dish}
            key={key}
            href={dish.url2}
            onClick={() => openLink(dish.url2)}
            target="_blank"
            rel="noreferrer">
            <div className={classes.imageContainer}>
              <img
                className={classes.image}
                src={dish.imgURL2}
                alt={dish.result2}
              />
            </div>
            <p className={classes.dishTitle}>{dish.result2}</p>
          </a>
          <a
            className={classes.dish}
            key={key}
            href={dish.url3}
            onClick={() => openLink(dish.url3)}
            target="_blank"
            rel="noreferrer">
            <div className={classes.imageContainer}>
              <img
                className={classes.image}
                src={dish.imgURL3}
                alt={dish.result3}
              />
            </div>
            <p className={classes.dishTitle}>{dish.result3}</p>
          </a>
        </>
      )
    }
    if (dish.noOfDishes === 2) {
      return (
        <>
          <a
            className={classes.dish}
            key={key}
            href={dish.url}
            onClick={() => openLink(dish.url)}
            target="_blank"
            rel="noreferrer">
            <div className={classes.imageContainer}>
              <img
                className={classes.image}
                src={dish.imgURL}
                alt={dish.result1}
              />
            </div>
            <p className={classes.dishTitle}>{dish.result1}</p>
          </a>
          <a
            className={classes.dish}
            key={key}
            href={dish.url2}
            onClick={() => openLink(dish.url2)}
            target="_blank"
            rel="noreferrer">
            <div className={classes.imageContainer}>
              <img
                className={classes.image}
                src={dish.imgURL2}
                alt={dish.result2}
              />
            </div>
            <p className={classes.dishTitle}>{dish.result2}</p>
          </a>
        </>
      )
    }
    return (
      <a
        className={classes.dish}
        key={key}
        href={dish.url}
        onClick={() => openLink(dish.url)}
        target="_blank"
        rel="noreferrer">
        <div className={classes.imageContainer}>
          <img className={classes.image} src={dish.imgURL} alt={dish.result} />
        </div>
        <p className={classes.dishTitle}>{dish.result}</p>
      </a>
    )
  }

  return (
    section === "results" && (
      <footer className={classes.container} onAnimationEnd={onAnimationEnd}>
        <div className={classes.about}>
          <h2 className={classes.heading}>
            Here is your personalized Thanksgiving menu.
          </h2>
          <p className={classes.description}>We curated it just for you.</p>
        </div>
        <div className={classes.menu}>
          {renderData.map((category) =>
            category.dishes.length && category.title !== "skip" ? (
              <div className={classes.subMenu} key={category.id}>
                <h3 className={classes.subMenuHeading}>{category.title}</h3>
                <div className={classes.dishesContainer}>
                  {category.dishes.map((dish, j) => renderDish(j, dish))}
                </div>
              </div>
            ) : null
          )}
        </div>
        <CopyToClipboard url={url} />
        <span style={{ marginBottom: "1rem" }}>
          Not quite what you were craving?
        </span>
        <AnswerButton
          id={"restartQuizButton"}
          text="START OVER"
          onClick={restartQuiz}
        />
        <div className={classes.credits}>
          <span>Editorial: Maggie Hoffman</span>
          <span>Design: Lily Boyce</span>
          <span>Development: Raagul Nagendran, Lily Boyce</span>
        </div>
      </footer>
    )
  )
}

export default withStyles(styles)(Results)
